import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import './MagazineProductSpread.css'
import { useMagazineProduct } from './useMagazineProduct'

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

function FlyBubbles({ items }) {
  if (!items?.length) return null
  return createPortal(
    <>
      {items.map((fly) => (
        <div
          key={fly.id}
          className="magazine-fly-bubble"
          style={{
            left: `${fly.fromX}px`,
            top: `${fly.fromY}px`,
            '--fly-dx': `${fly.dx}px`,
            '--fly-dy': `${fly.dy}px`,
          }}
          aria-hidden="true"
        >
          {fly.image ? (
            <img src={fly.image} alt="" className="magazine-fly-bubble-image" />
          ) : (
            <span className="magazine-fly-bubble-dot" />
          )}
        </div>
      ))}
    </>,
    document.body
  )
}

/**
 * Magazine spread: full-width editorial block for one product.
 * Image collage (hero + small images, click to swap) beside an info panel
 * with ghost numeral, name, description, variant chips, price, add-to-cart.
 */
function MagazineProductSpread({ product, index = 0, reversed = false, cartCount = 0, onAddToCart }) {
  const {
    images,
    variants,
    selectedVariantId,
    selectVariant,
    selectedVariant,
    pricing,
    isAnimating,
    handleAddToCartClick,
    flyItems,
  } = useMagazineProduct(product, { onAddToCart, cartCount })

  const [heroIndex, setHeroIndex] = useState(0)

  useEffect(() => {
    setHeroIndex(0)
  }, [product?.id, product?._id])

  const heroImage = images[heroIndex]?.url ?? images[0]?.url ?? null
  const smallImages = images.filter((_, i) => i !== heroIndex)
  const description = (product?.description ?? '').trim()
  const numeral = String(index + 1).padStart(2, '0')

  return (
    <article
      className={`magazine-spread${reversed ? ' magazine-spread--reversed' : ''}`}
      aria-label={product?.name ?? 'Товар'}
    >
      <div className="magazine-collage">
        {heroImage ? (
          <div className="magazine-hero">
            <img src={heroImage} alt={product?.name ?? ''} className="magazine-hero-image" draggable={false} />
          </div>
        ) : null}
        {smallImages.length > 0 ? (
          <div className="magazine-smalls" role="listbox" aria-label="Фотографии товара">
            {smallImages.map((img) => {
              const originalIndex = images.findIndex((item) => item.url === img.url && item.index === img.index)
              return (
                <button
                  key={`${img.url}-${img.index}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="magazine-small"
                  onClick={() => setHeroIndex(originalIndex >= 0 ? originalIndex : 0)}
                  aria-label="Показать фото"
                >
                  <img src={img.url} alt="" className="magazine-small-image" draggable={false} />
                </button>
              )
            })}
          </div>
        ) : null}
      </div>

      <div className="magazine-info">
        <div className="magazine-numeral" aria-hidden="true">{numeral}</div>
        <div className="magazine-info-body">
          <h2 className="magazine-title">{product?.name}</h2>
          <div className="magazine-rule" aria-hidden="true" />
          {description ? (
            <p className="magazine-description">{description}</p>
          ) : null}

          {variants.length > 1 ? (
            <div className="magazine-variants" role="listbox" aria-label="Варианты">
              {variants.map((variant) => {
                const variantId = variant.id ?? variant._id
                const selected = selectedVariantId === variantId
                const vPrice = parsePrice(variant.price)
                return (
                  <button
                    key={variantId}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`magazine-variant${selected ? ' magazine-variant--selected' : ''}`}
                    onClick={() => selectVariant(variantId)}
                  >
                    <span className="magazine-variant-title">
                      {String(variant.title ?? '').trim() || 'Обычный'}
                    </span>
                    <span className="magazine-variant-price">
                      {Number.isFinite(vPrice) ? `${vPrice.toFixed(2)} сом` : '—'}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : null}

          <div className="magazine-pricing">
            <span className="magazine-price">
              {Number.isFinite(pricing.currentPrice)
                ? `${pricing.pricePrefix}${pricing.currentPrice.toFixed(2)} сом${pricing.priceSuffix || ''}`
                : '—'}
            </span>
            {pricing.oldPrice != null && (
              <span className="magazine-price-old" aria-label="Прежняя цена">
                {pricing.oldPrice.toFixed(2)} сом
              </span>
            )}
          </div>

          <button
            type="button"
            className={`magazine-add${isAnimating ? ' magazine-add--animating' : ''}`}
            onClick={handleAddToCartClick}
            disabled={!selectedVariant}
          >
            В корзину
          </button>
        </div>
      </div>

      <FlyBubbles items={flyItems} />
    </article>
  )
}

export default MagazineProductSpread
